import {
  defaultConfig,
  type AcousticConfig,
} from '../../core';
import {
  getTheme,
  setTheme,
  themeLabels,
  themeOrder,
  type ThemeName,
} from '../../theme';

type ChangeFn = () => void;

/**
 * SettingsDrawer — equipment "SETUP" drawer.
 * - Right-side slide on desktop, bottom-sheet on mobile (CSS-driven).
 * - Hosts: theme picker, F0..F3 inputs, baud input.
 * - Inputs keep the same IDs the views look up (`#cfg-f0`..`#cfg-f3`, `#cfg-baud`),
 *   so SenderView/ReceiverView config-readers continue to work.
 */
export class SettingsDrawer {
  readonly el: HTMLElement;
  readonly backdrop: HTMLDivElement;
  private themeBtns: HTMLButtonElement[] = [];
  private onChangeFn: ChangeFn | null = null;
  private open = false;

  constructor() {
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'settings-backdrop';
    this.backdrop.addEventListener('click', () => this.close());

    this.el = document.createElement('aside');
    this.el.className = 'settings-drawer';
    this.el.setAttribute('aria-hidden', 'true');
    this.el.setAttribute('aria-label', 'Setup');

    const head = document.createElement('div');
    head.className = 'settings-drawer__head';
    head.innerHTML = `
      <span class="settings-drawer__title">SETUP</span>
      <button class="settings-drawer__close" type="button" aria-label="Close">×</button>
    `;
    head
      .querySelector<HTMLButtonElement>('.settings-drawer__close')!
      .addEventListener('click', () => this.close());
    this.el.appendChild(head);

    const body = document.createElement('div');
    body.className = 'settings-drawer__body';

    body.appendChild(this.buildSection('Theme', this.buildThemeRow()));
    body.appendChild(
      this.buildSection('Frequencies (Hz)', this.buildFreqGrid()),
    );
    body.appendChild(this.buildSection('Modulation', this.buildBaudRow()));

    this.el.appendChild(body);

    // Live-update listeners on inputs
    this.el.querySelectorAll<HTMLInputElement>('.config-cell__input').forEach(
      (inp) => {
        inp.addEventListener('input', () => this.onChangeFn?.());
      },
    );

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.open) this.close();
    });
  }

  private buildSection(title: string, content: HTMLElement): HTMLElement {
    const sec = document.createElement('section');
    sec.className = 'settings-drawer__section';
    const h = document.createElement('h3');
    h.className = 'settings-drawer__section-title';
    h.textContent = title.toUpperCase();
    sec.appendChild(h);
    sec.appendChild(content);
    return sec;
  }

  private buildThemeRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'theme-picker';
    for (const name of themeOrder) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'theme-picker__btn';
      btn.dataset.theme = name;
      btn.innerHTML = `
        <span class="theme-picker__swatch" data-theme-swatch="${name}">
          <span class="theme-picker__dot" data-trace="0"></span>
          <span class="theme-picker__dot" data-trace="1"></span>
          <span class="theme-picker__dot" data-trace="2"></span>
          <span class="theme-picker__dot" data-trace="3"></span>
        </span>
        <span class="theme-picker__label">${themeLabels[name]}</span>
      `;
      btn.addEventListener('click', () => {
        setTheme(name);
        this.refreshActiveTheme();
      });
      row.appendChild(btn);
      this.themeBtns.push(btn);
    }
    return row;
  }

  private refreshActiveTheme(): void {
    const cur = getTheme();
    for (const btn of this.themeBtns) {
      btn.classList.toggle('is-active', btn.dataset.theme === cur);
    }
  }

  private buildFreqGrid(): HTMLElement {
    const grid = document.createElement('div');
    grid.className = 'config-grid';
    for (let i = 0; i < 4; i++) {
      const cell = document.createElement('label');
      cell.className = 'config-cell';
      cell.innerHTML = `
        <span class="config-cell__label">
          <span class="config-cell__chip" data-freq="${i}"></span>F${i}
        </span>
        <input class="config-cell__input" id="cfg-f${i}"
               type="number" inputmode="numeric"
               value="${defaultConfig.freqs[i]}" min="500" max="8000" step="100" />
        <span class="config-cell__unit">Hz</span>
      `;
      grid.appendChild(cell);
    }
    return grid;
  }

  private buildBaudRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'config-grid';
    const cell = document.createElement('label');
    cell.className = 'config-cell';
    cell.innerHTML = `
      <span class="config-cell__label">Baud</span>
      <input class="config-cell__input" id="cfg-baud"
             type="number" inputmode="numeric"
             value="${defaultConfig.baudRate}" min="5" max="100" step="5" />
      <span class="config-cell__unit">sym/s</span>
    `;
    row.appendChild(cell);
    return row;
  }

  onChange(fn: ChangeFn): void {
    this.onChangeFn = fn;
  }

  toggle(): void {
    if (this.open) this.close();
    else this.openDrawer();
  }

  openDrawer(): void {
    this.open = true;
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    this.backdrop.classList.add('is-open');
    this.refreshActiveTheme();
  }

  close(): void {
    this.open = false;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    this.backdrop.classList.remove('is-open');
  }

  /** Read current config from the drawer's inputs (or defaults if missing). */
  readConfig(): Partial<AcousticConfig> {
    const get = (id: string, fb: number): number => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      const n = el ? parseInt(el.value) : NaN;
      return Number.isFinite(n) ? n : fb;
    };
    return {
      freqs: [
        get('cfg-f0', defaultConfig.freqs[0]),
        get('cfg-f1', defaultConfig.freqs[1]),
        get('cfg-f2', defaultConfig.freqs[2]),
        get('cfg-f3', defaultConfig.freqs[3]),
      ],
      baudRate: get('cfg-baud', defaultConfig.baudRate),
    };
  }
}

export function applyThemeSwatches(): void {
  // Each .theme-picker__swatch[data-theme-swatch=...] gets an inline data attribute;
  // CSS uses attribute selectors to color the trace dots in that theme's palette.
  // (No JS color manipulation needed — CSS holds the truth.)
  void 0 as unknown as ThemeName;
}
